import { useApolloClient } from '@apollo/client';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { LinearProgress, Snackbar } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ResponseGq,
  VariablesGq,
  gqTag,
} from '../../../../../graphql/projects/queries/useGetKeywordsForCopyGq';
import { copyToClipboard } from '../../../../../logic/otherUtilities/copyTextToClipboard';
import { formatNumber } from '../../../../../logic/otherUtilities/formatting/formatNumber';
import DataTableToolbarButton from '../../../../voxel/DataTableToolbar/DataTableToolbarButton';
import { VoxelPopover } from '../../../../voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../../voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../../voxel/Popover/VoxelPopoverTrigger';
import VoxelMenuItemContainer from '../../../../voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelPopoverMenuItem from '../../../../voxel/PopoverMenu/VoxelPopoverMenuItem';
import { KeywordTableState } from '../logic/useKeywordTableState';
import VoxelMenuDivider from '../../../../voxel/PopoverMenu/VoxelMenuDivider';

export interface CopyButtonProps {
  selectedCount?: number;
  tableState: KeywordTableState;
  selectionActive?: boolean;
}

export const CopyButton: React.FC<CopyButtonProps> = ({ selectedCount, tableState, selectionActive }) => {
  const copyLimit = 50000;

  // Snackbars
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState<string>();
  useEffect(() => {
    if (!showSnackbar) {
      return;
    }

    const timeout = setTimeout(() => setShowSnackbar(false), 1250);
    return () => clearTimeout(timeout);
  }, [showSnackbar]);

  useEffect(() => {
    if (!errorSnackbarMessage?.length) {
      return;
    }
    const timeout = setTimeout(() => setErrorSnackbarMessage(undefined), 5000);
    return () => clearTimeout(timeout);
  }, [errorSnackbarMessage]);

  // Data fetching
  const apolloClient = useApolloClient();
  const getData = useCallback(
    () =>
      apolloClient.query<ResponseGq, VariablesGq>({
        query: gqTag,
        variables: {
          command: {
            keywordQuery: {
              filtering: tableState.backendFilterState,
              sorting: tableState.sortingState,
              selecting: tableState.backendSelectionState,
              paging: {
                page: 0,
                amountPerPage: copyLimit,
              },
            },
          },
        },
      }),
    [tableState.backendFilterState, tableState.sortingState, tableState.backendSelectionState]
  );

  // Main function
  const [loading, setLoading] = useState(false);
  const handleCopyToClipboard = useCallback(
    (seFormat: boolean) => {
      if (loading) return;
      setLoading(true);

      getData()
        .then((result) => {
          // Convert to tab separated data
          const conversion = result.data.projects.getKeywordsForCopy.currencyConversion;
          const currency = result.data.projects.getKeywordsForCopy.currency?.toLowerCase() ?? 'sek';
          const remappedData = result.data.projects.getKeywordsForCopy.keywords
            .map(
              (kw) =>
                `${kw.keyword}\t${kw.volume}\t${seFormat
                  ? (kw.competition).toString().replace('.', ',')
                  : kw.competition
                }\t${seFormat
                  ? ((kw.cPC / 1000000) * conversion).toString().replace('.', ',')
                  : (kw.cPC / 1000000) * conversion
                }\t${seFormat
                  ? ((kw.bidLow / 1000000) * conversion).toString().replace('.', ',')
                  : (kw.bidLow / 1000000) * conversion
                }\t${seFormat
                  ? ((kw.bidHigh / 1000000) * conversion).toString().replace('.', ',')
                  : (kw.bidHigh / 1000000) * conversion
                }`
            )
            .join('\n');

          const clipboardResult =
            `keyword\tvolume\tcompetition\tcpc_legacy_${currency}\tbid_low_${currency}\tbid_high_${currency}\n` +
            remappedData;

          const copyResult = copyToClipboard(clipboardResult);
          if (copyResult) {
            setShowSnackbar(true);
          } else {
            setErrorSnackbarMessage('Copy not supported by browser - try Chrome instead');
          }
        })
        .then(() => tableState.handleResetSelection())
        .catch(() => setErrorSnackbarMessage('Copy error - something went wrong'))
        .finally(() => setLoading(false));
    },
    [getData]
  );
  const handleCopyKeywordsOnly = useCallback(() => {
    if (loading) return;
    setLoading(true);
    getData()
      .then((result) => {
        // Convert to line separated data
        const remappedData = result.data.projects.getKeywordsForCopy.keywords
          .map((kw) => kw.keyword)
          .join('\n');

        const copyResult = copyToClipboard(remappedData);
        if (copyResult) {
          setShowSnackbar(true);
        } else {
          setErrorSnackbarMessage('Copy not supported by browser - try Chrome instead');
        }
      })
      .then(() => tableState.handleResetSelection())
      .catch(() => setErrorSnackbarMessage('Copy error - something went wrong'))
      .finally(() => setLoading(false));
  }, [getData, loading]);

  useEffect(() => {
    // Add handlers on mount
    const keyDownHandler = (event: KeyboardEvent) => {
      // Don't copy if some kind of input element is focused
      if (
        ['input', 'select', 'textarea'].includes(document.activeElement?.tagName?.toLowerCase())
      ) {
        if (
          document.activeElement.tagName.toLowerCase() === 'input' &&
          document.activeElement.attributes.getNamedItem('type')?.value?.toLowerCase() ===
          'checkbox'
        ) {
          // This is probably the selection checkbox being pressed...
        } else {
          // ... Don't copy, we don't know if the user is in an input field or something....
          return;
        }
      }

      // Make sure the right buttons are selected
      if (event.key === 'c' && (event.metaKey || event.ctrlKey)) {
        if (loading) {
          setErrorSnackbarMessage('Could not copy - Previous still in progress');
        } else {
          // Execute copy
          handleCopyKeywordsOnly();
        }
      }
    };

    window.addEventListener('keydown', keyDownHandler, false);

    // Remove handlers on unmount
    return () => window.removeEventListener('keydown', keyDownHandler);
  }, [handleCopyKeywordsOnly, loading]);

  return (
    <>
      <Snackbar
        message={'Copied to clipboard'}
        open={showSnackbar}
        onClick={() => setShowSnackbar(false)}
      />

      <Snackbar
        open={!!errorSnackbarMessage?.length}
        onClick={() => setErrorSnackbarMessage(undefined)}
        message={errorSnackbarMessage}
      />
      <VoxelPopover>
        <VoxelPopoverTrigger enableActive>
          <DataTableToolbarButton
            tooltip={
              <>
                Copy selected keywords{' '}
                <span className="opacity-70">(max {formatNumber(copyLimit)})</span>
              </>
            }
            iconProp={faCopy}
            disabled={selectedCount > copyLimit}
            effect={selectionActive ? 'primary' : undefined}
            effectAnimate={false}
          />
        </VoxelPopoverTrigger>
        <VoxelPopoverContent className="w-80">
          <VoxelMenuItemContainer>
            <VoxelPopoverMenuItem
              title="Copy Selected Keywords"
              keyboardShortcut="⌘ + C"
              onClick={() => {
                handleCopyKeywordsOnly();
              }}
            />
            <VoxelMenuDivider />
            <VoxelPopoverMenuItem
              title="Copy Selected Keywords (Excel)"
              onClick={() => {
                handleCopyToClipboard(false);
              }}
            />

            <VoxelPopoverMenuItem
              title="Copy Selected Keywords (Excel SE)"
              onClick={() => {
                handleCopyToClipboard(true);
              }}
            />
          </VoxelMenuItemContainer>
          {loading && <LinearProgress />}
        </VoxelPopoverContent>
      </VoxelPopover>
    </>
  );
};

export default CopyButton;
